import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { channelIdFrom } from '../../Components/IngestRequest/useIngestRequest'
import { ButtonOutline } from '../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import classes from './NewRequestCard.module.css'

/**
 * Ask for an artist synchronization.
 *
 * One field, because the service takes one thing: a YouTube channel id. A
 * link is fine - we pull the id out and show it back before anything is sent,
 * so a wrong paste is caught by eye rather than by a failed request.
 */

const WHY = {
  empty: 'Paste the artist’s YouTube channel link, or their UC… channel id.',
  not_an_artist: 'That link points at a song or an album. Open the artist’s channel and copy that link.',
  needs_channel_id:
    'We can read that, but ingestion needs the YouTube channel. Open the artist on YouTube Music and copy the /channel/UC… link.',
  legacy_channel_url: 'That is an old-style channel address. Copy the link that has /@ or /channel/ in it.',
  looks_like_a_name: 'Paste a YouTube channel link or ID, not a name.',
  partial_channel_id: 'That channel ID is incomplete — it should be UC followed by 22 characters.',
  unknown_host: 'We only read YouTube links here.',
  not_a_url: 'That is not a link or an id we can read.',
  no_identifier: 'We could not find a channel id in that link.',
  radio_list_no_video: 'That is a radio queue. Open the artist’s channel and copy that link.',
  playlist_not_a_release: 'That is a playlist. Open the artist’s channel and copy that link.',
}

export default function NewRequestCard({ onSubmit, isSubmitting, error }) {
  const [value, setValue] = useState('')
  const [forceRefresh, setForceRefresh] = useState(false)

  const found = value.trim() ? channelIdFrom(value) : null
  const canSubmit = Boolean(found && found.ok) && !isSubmitting

  function handleSubmit(e) {
    e.preventDefault()
    if (!canSubmit) return
    onSubmit(value.trim(), { forceRefresh })
  }

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <div className={classes.formField}>
        <label className={classes.formLabel} htmlFor="ingest-request-value">
          The artist’s YouTube channel
        </label>
        <input
          id="ingest-request-value"
          className={classes.channelInput}
          type="text"
          autoComplete="off"
          placeholder="https://music.youtube.com/channel/UC… or a UC… id"
          value={value}
          onChange={e => setValue(e.target.value)}
        />
        {found && found.ok && (
          <span className={classes.resolvedNextOk}>
            Channel ID: <code className={classes.resolvedId}>{found.channelId}</code>
          </span>
        )}
        {found && !found.ok && (
          <span className={classes.helpBad}>{WHY[found.reason] || WHY.empty}</span>
        )}
      </div>

      <div className={classes.formField}>
        <label className={classes.checkLabel} htmlFor="ingest-request-fresh">
          <input
            id="ingest-request-fresh"
            type="checkbox"
            checked={forceRefresh}
            onChange={e => setForceRefresh(e.target.checked)}
          />{' '}
          Re-fetch from YouTube
        </label>
        <span className={classes.help}>
          If we fetched them in the last 24 hours, we’ll reuse that. Tick this if they’ve released
          something since.
        </span>
      </div>

      <span className={classes.help}>
        If this artist is already syncing, we’ll open that request instead.
      </span>

      {error && <span className={classes.formError}>{error}</span>}

      <div className={classes.formActions}>
        <ButtonOutline
          color="pink"
          size="small"
          type="submit"
          disable={!canSubmit}
          text={isSubmitting ? 'STARTING…' : 'START SYNCHRONIZATION'}
        />
      </div>
    </form>
  )
}

NewRequestCard.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  error: PropTypes.string,
}
